import moment from "moment";

export const generateDayHeaders = (month, year) => {
  const totalDays = moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
  return Array.from({ length: totalDays }, (_, i) => String(i + 1));
};

export const buildSheet = ({ sheetName, teamLabel, data, month, year }) => {
  const dayHeaders = generateDayHeaders(month, year);

  return {
    name: sheetName,
    headers: [teamLabel, ...dayHeaders, "Total", "Proyeksi"],
    data: data.map((item, index) => {
      const row = {
        No: index + 1,
        [teamLabel]: item.name,
      };

      dayHeaders.forEach((day) => {
        row[day] = item[day] ?? "Rp. 0";
      });

      row.Total = item.total ?? "Rp. 0";
      row.Proyeksi = item.proyeksi ?? "Rp. 0";

      return row;
    }),
  };
};

export const buildExcelRequestBody = ({
  month,
  year,
  dataTable,
  dataTableSA,
}) => {
  const fileName = `${year}_${month.label}_Laporan_Part_Harian`;

  return {
    filename: fileName,
    sheets: [
      buildSheet({
        sheetName: "Mekanik",
        teamLabel: "Tim Mekanik",
        data: dataTable,
        month: month.value,
        year,
      }),
      buildSheet({
        sheetName: "SA",
        teamLabel: "Tim SA",
        data: dataTableSA,
        month: month.value,
        year,
      }),
    ],
  };
};

export const buildExcelRequestBodyPartByMekanik = ({
  month,
  year,
  rawData,
  mekanik
}) => {
  const filename = `${year}_${month.label}_Laporan_Part_Harian_By_Mekanik_${mekanik.label}`;

  if (!rawData?.length) {
    return {
      filename,
      sheets: [],
    };
  }

  const mechanic = rawData[0];
  const partHarian = mechanic.partHarian || {};
  const partLainnyaMap = {};

  Object.values(partHarian).forEach((day) => {
    if (!day?.partLainnya) return;

    day.partLainnya.forEach((p) => {
      partLainnyaMap[p.partNameLainnya] = true;
    });
  });

  const partLainnyaList = Object.keys(partLainnyaMap);

  const headers = [
    {
      key: "tanggal",
      title: "Tanggal",
    },
    {
      title: "Total Part",
      children: [
        { key: "totalPart", title: "Harian" },
        { key: "akumulasiTotal", title: "Akumulasi" },
      ],
    },
    {
      title: "TIRE",
      children: [
        { key: "totalTire", title: "Harian" },
        { key: "akumulasiTire", title: "Akumulasi" },
        { key: "persenTire", title: "%sp" },
      ],
    },
    {
      title: "OIL",
      children: [
        { key: "totalOil", title: "Harian" },
        { key: "akumulasiOil", title: "Akumulasi" },
        { key: "persenOil", title: "%sp" },
      ],
    },
    {
      title: "BATTERY",
      children: [
        { key: "totalBattery", title: "Harian" },
        { key: "akumulasiBattery", title: "Akumulasi" },
        { key: "persenBattery", title: "%sp" },
      ],
    },
  ];

  partLainnyaList.forEach((name) => {
    headers.push({
      title: name,
      children: [
        { key: `totalPartLainnya_${name}`, title: "Harian" },
        { key: `akumulasiPartLainnya_${name}`, title: "Akumulasi" },
        { key: `persenPartLainnya_${name}`, title: "%sp" },
      ],
    });
  });

  const daysInMonth = moment(`${year}-${month.value}`, "YYYY-M").daysInMonth();

  const data = [];

  for (let i = 1; i <= daysInMonth; i += 1) {
    const daily = partHarian[i] || {};

    const row = {
      tanggal: moment(`${year}-${month.value}-${i}`, "YYYY-M-D").format(
        "DD/MM/YYYY",
      ),

      totalPart: Number(daily.totalPart || 0),
      akumulasiTotal: Number(daily.akumulasiTotal || 0),

      totalTire: Number(daily.totalTire || 0),
      akumulasiTire: Number(daily.akumulasiTire || 0),
      persenTire: Number(daily.persenTire || 0),

      totalOil: Number(daily.totalOil || 0),
      akumulasiOil: Number(daily.akumulasiOil || 0),
      persenOil: Number(daily.persenOil || 0),

      totalBattery: Number(daily.totalBattery || 0),
      akumulasiBattery: Number(daily.akumulasiBattery || 0),
      persenBattery: Number(daily.persenBattery || 0),
    };

    // default part lainnya
    partLainnyaList.forEach((name) => {
      row[`totalPartLainnya_${name}`] = 0;
      row[`akumulasiPartLainnya_${name}`] = 0;
      row[`persenPartLainnya_${name}`] = 0;
    });

    // isi dari API
    if (daily.partLainnya) {
      daily.partLainnya.forEach((p) => {
        const key = p.partNameLainnya;
        row[`totalPartLainnya_${key}`] = Number(p.totalPartLainnya || 0);
        row[`akumulasiPartLainnya_${key}`] = Number(
          p.akumulasiPartLainnya || 0,
        );
        row[`persenPartLainnya_${key}`] = Number(p.persenPartLainnya || 0);
      });
    }

    data.push(row);
  }

  return {
    filename,
    sheets: [
      {
        name: "Laporan Part Harian By Mekanik",
        headers,
        data,
      },
    ],
  };
};

export const buildExcelRequestBodyJualPartMTD = ({
  month,
  year,
  dataTable,
  dataTableSA,
}) => {
  const filename = `${year}_${month.label}_Laporan_Jual_Part_MTD`;

  return {
    filename,
    sheets: [
      {
        name: "Tim Mekanik",
        headers: [
          "No",
          "Tim Mekanik",
          "Last Month",
          "This Month",
          "Growth",
          "Proyeksi",
        ],
        data: (dataTable || []).map((item, index) => ({
          No: index + 1,
          "Tim Mekanik": item.employeeName,
          "Last Month": item.lastMonthValue ?? 0,
          "This Month": item.thisMonthValue ?? 0,
          Growth: item.growthPercent ?? 0,
          Proyeksi: item.projectionSales ?? 0,
        })),
      },
      {
        name: "Tim SA",
        headers: [
          "No",
          "Tim SA",
          "Last Month",
          "This Month",
          "Growth",
          "Proyeksi",
        ],
        data: (dataTableSA || []).map((item, index) => ({
          No: index + 1,
          "Tim SA": item.employeeName,
          "Last Month": item.lastMonthValue ?? 0,
          "This Month": item.thisMonthValue ?? 0,
          Growth: item.growthPercent ?? 0,
          Proyeksi: item.projectionSales ?? 0,
        })),
      },
    ],
  };
};

export const buildExcelRequestBodyJualPartMTDByMekanik = ({
  month,
  year,
  dataTable,
  mekanik,
}) => {
  const filename = `${year}_${month.label}_Laporan_Jual_Part_MTD_By_Mekanik_${mekanik.label}`;

  return {
    filename,
    sheets: [
      {
        name: "Laporan Jual Part MTD By Mekanik",
        headers: [
          "No",
          "Category",
          "Last Month",
          "This Month",
          "Growth",
          "Proyeksi",
        ],
        data: (dataTable || []).map((item, index) => ({
          No: index + 1,
          Category: item.partCategory,
          "Last Month": item.lastMonthValue ?? 0,
          "This Month": item.thisMonthValue ?? 0,
          Growth: item.growthPercent ?? 0,
          Proyeksi: item.projectionSales ?? 0,
        })),
      },
    ],
  };
};

const MONTH_HEADERS = [
  { key: "jan", label: "JANUARI" },
  { key: "feb", label: "FEBRUARI" },
  { key: "mar", label: "MARET" },
  { key: "apr", label: "APRIL" },
  { key: "may", label: "MEI" },
  { key: "jun", label: "JUNI" },
  { key: "jul", label: "JULI" },
  { key: "aug", label: "AGUSTUS" },
  { key: "sep", label: "SEPTEMBER" },
  { key: "oct", label: "OKTOBER" },
  { key: "nov", label: "NOVEMBER" },
  { key: "dec", label: "DESEMBER" },
];

export const buildExcelRequestBodyJualPartYTD = ({
  year,
  dataTable = [],
  dataTableSA = [],
}) => {
  const yearValue = year?.value || year;

  const filename = `${yearValue}_Laporan_Jual_Part_YTD`;
  const sheets = [];

  if (Array.isArray(dataTable) && dataTable.length > 0) {
    sheets.push({
      name: `Laporan Jual Part YTD ${yearValue}`,
      headers: [
        "No",
        "Tim Mekanik",
        ...MONTH_HEADERS.map((m) => m.label),
        `YTD ${yearValue}`,
      ],
      data: dataTable.map((item, index) => {
        const row = {
          No: index + 1,
          "Tim Mekanik": item.paymentName,
        };

        MONTH_HEADERS.forEach(({ key, label }) => {
          row[label] = item[key] ?? 0;
        });

        row[`YTD ${yearValue}`] = item.total ?? 0;

        return row;
      }),
    });
  }

  if (Array.isArray(dataTableSA) && dataTableSA.length > 0) {
    sheets.push({
      name: `Laporan Jual Part YTD ${yearValue}`,
      headers: [
        "No",
        "Tim SA",
        ...MONTH_HEADERS.map((m) => m.label),
        `YTD ${yearValue}`,
      ],
      data: dataTableSA.map((item, index) => {
        const row = {
          No: index + 1,
          "Tim SA": item.paymentName,
        };

        MONTH_HEADERS.forEach(({ key, label }) => {
          row[label] = item[key] ?? 0;
        });

        row[`YTD ${yearValue}`] = item.total ?? 0;

        return row;
      }),
    });
  }

  return {
    filename,
    sheets, 
  };
};

const QUARTER_HEADERS = [
  { key: "q1y2", label: "Q1 Y-2" },
  { key: "q2y2", label: "Q2 Y-2" },
  { key: "q3y2", label: "Q3 Y-2" },
  { key: "q4y2", label: "Q4 Y-2" },
  { key: "q1y1", label: "Q1 Y-1" },
  { key: "q2y1", label: "Q2 Y-1" },
  { key: "q3y1", label: "Q3 Y-1" },
  { key: "q4y1", label: "Q4 Y-1" },
  { key: "q1y", label: "Q1 Y" },
  { key: "q2y", label: "Q2 Y" },
  { key: "q3y", label: "Q3 Y" },
  { key: "q4y", label: "Q4 Y" },
];

export const buildExcelRequestBodyJualPartL3Y = ({
  year,
  dataTable = [],
  dataTableSA = [],
}) => {
  const yearValue = year?.value || year;
  const filename = `${yearValue}_Laporan_Jual_Part_3Tahun`;
  const sheets = [];

  if (Array.isArray(dataTable) && dataTable.length > 0) {
    sheets.push({
      name: `Laporan Jual Part 3 Tahun Terakhir`,
      headers: [
        "No",
        "Tim Mekanik",
        ...QUARTER_HEADERS.map((q) => q.label),
      ],
      data: dataTable.map((item, index) => {
        const row = {
          No: index + 1,
          "Tim Mekanik": item.name,
        };

        QUARTER_HEADERS.forEach(({ key, label }) => {
          row[label] = item[key] ?? "Rp. 0";
        });

        return row;
      }),
    });
  }

  if (Array.isArray(dataTableSA) && dataTableSA.length > 0) {
    sheets.push({
      name: `Laporan Jual Part 3 Tahun Terakhir`,
      headers: [
        "No",
        "Tim SA",
        ...QUARTER_HEADERS.map((q) => q.label),
      ],
      data: dataTableSA.map((item, index) => {
        const row = {
          No: index + 1,
          "Tim SA": item.name,
        };

        QUARTER_HEADERS.forEach(({ key, label }) => {
          row[label] = item[key] ?? "Rp. 0";
        });

        return row;
      }),
    });
  }

  return {
    filename,
    sheets, 
  };
};
